/**
 * Comercial → Empresas → detalhe (`/comercial/empresas/:id`).
 *
 * Mostra uma empresa da organização (`crm.companies`) com o que o import
 * da base pública de CNPJ grava nos satélites da migration 041: CNAEs, sócios
 * (QSA) e canais de contato — mais os negócios vinculados (`deals.company_id`)
 * via GET /crm/companies/:id/detalhes. Filiais (mesmo CNPJ básico) vêm ao vivo
 * da base pública; "Atualizar pela Receita" reimporta o CNPJ (idempotente).
 *
 * Reusa cl-card/cl-table/cl-state de clientes.css; o resto fica em empresas.css.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import { getCompanyDetails, getEstabelecimentosPublicos, importarEmpresas } from '../../db/api.js'
import { fmtCnpj } from './Empresas.jsx'
import DealModal from './DealModal.jsx'
import './clientes.css'
import './empresas.css'

const SITUACAO = { ativa: 'Ativa', baixada: 'Baixada', suspensa: 'Suspensa', inapta: 'Inapta', nula: 'Nula' }
const PORTE = { mei: 'MEI', me: 'Microempresa', epp: 'Pequeno porte', demais: 'Demais' }
const CANAL = { phone: 'Telefone', email: 'E-mail', linkedin: 'LinkedIn', instagram: 'Instagram', website: 'Site', other: 'Outro contato' }
const STATUS_NEGOCIO = { aberto: 'Aberto', ganho: 'Ganho', perdido: 'Perdido' }

const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : null)
const fmtMoney = (v) => (v == null || v === '' ? null : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }))
const fmtCep = (v) => {
  const d = String(v || '').replace(/\D/g, '')
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : v
}
const fmtCnae = (v) => {
  const d = String(v || '').replace(/\D/g, '')
  return d.length === 7 ? `${d.slice(0, 4)}-${d.slice(4, 5)}/${d.slice(5)}` : v
}

function Field({ label, value }) {
  return (
    <div className="emp-field">
      <span className="emp-field-label">{label}</span>
      <span className="emp-field-value">{value || '—'}</span>
    </div>
  )
}

export default function EmpresaView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [company, setCompany] = useState(null)
  const [err, setErr] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [notice, setNotice] = useState('')
  const [filiais, setFiliais] = useState(null)
  const [filiaisErr, setFiliaisErr] = useState('')
  const [importingCnpj, setImportingCnpj] = useState('')
  const [newDealOpen, setNewDealOpen] = useState(false)

  const load = useCallback(() => getCompanyDetails(id), [id])

  useEffect(() => {
    let alive = true
    setCompany(null); setErr(''); setNotice('')
    load()
      .then((data) => { if (alive) setCompany(data) })
      .catch((e) => { if (alive) setErr(e.status === 404 ? 'Empresa não encontrada.' : e.message) })
    return () => { alive = false }
  }, [load])

  const cnpj = company?.cnpj
  useEffect(() => {
    if (!cnpj) return
    let alive = true
    setFiliais(null); setFiliaisErr('')
    getEstabelecimentosPublicos(cnpj)
      .then((data) => { if (alive) setFiliais(data) })
      .catch((e) => { if (alive) setFiliaisErr(e.status === 503 ? 'Base pública de CNPJ indisponível no momento.' : e.message) })
    return () => { alive = false }
  }, [cnpj])

  // Reimport do CNPJ: traz da Receita o que mudou (e os campos que imports
  // antigos não gravavam). Não duplica — upsert por CNPJ.
  const atualizar = async () => {
    if (!cnpj || refreshing) return
    setRefreshing(true); setNotice('')
    try {
      const { imported } = await importarEmpresas([cnpj])
      if (!imported?.length) throw new Error('CNPJ não encontrado na base pública.')
      setCompany(await load())
      setNotice('Dados atualizados pela Receita Federal.')
    } catch (e) {
      setNotice(e.status === 503 ? 'Base pública de CNPJ indisponível no momento.' : e.message)
    } finally {
      setRefreshing(false)
    }
  }

  const importarFilial = async (filialCnpj) => {
    if (importingCnpj) return
    setImportingCnpj(filialCnpj)
    try {
      const { imported } = await importarEmpresas([filialCnpj])
      if (imported?.length) {
        setFiliais((f) => f && { ...f, items: f.items.map((it) => (it.cnpj === filialCnpj ? { ...it, already_imported: true } : it)) })
      }
    } catch (e) {
      setFiliaisErr(e.message)
    } finally {
      setImportingCnpj('')
    }
  }

  const title = company?.nome || 'Empresa'
  const breadcrumb = [
    { label: 'Comercial' },
    { label: 'Empresas', to: '/comercial/empresas' },
    { label: title },
  ]

  if (err || !company) {
    return (
      <Layout title={title} breadcrumb={breadcrumb}>
        {err ? <div className="cl-error">{err}</div> : <div className="cl-state">Carregando…</div>}
      </Layout>
    )
  }

  const endereco = [
    company.logradouro && [company.logradouro, company.numero].filter(Boolean).join(', '),
    company.complemento,
    company.bairro,
    [company.cidade, company.estado].filter(Boolean).join(' / '),
    company.cep && `CEP ${fmtCep(company.cep)}`,
  ].filter(Boolean).join(' · ')

  return (
    <Layout title={title} breadcrumb={breadcrumb}>
      <div className="cl-card emp-head">
        <div>
          <h2 className="emp-head-name">{company.nome}</h2>
          {company.razao_social && company.razao_social !== company.nome && (
            <p className="emp-head-sub">{company.razao_social}</p>
          )}
        </div>
        <div className="emp-head-meta">
          {company.cnpj && <span className="emp-chip">{fmtCnpj(company.cnpj)}</span>}
          {company.situacao_cadastral && (
            <span className={`emp-chip${company.situacao_cadastral === 'ativa' ? ' ok' : ' warn'}`}>
              {SITUACAO[company.situacao_cadastral] || company.situacao_cadastral}
            </span>
          )}
          {company.fonte === 'cnpj_lookup' && <span className="emp-chip">Importada da Receita</span>}
          {company.cnpj && (
            <button type="button" className="cl-new" disabled={refreshing} onClick={atualizar}>
              {refreshing ? 'Atualizando…' : 'Atualizar pela Receita'}
            </button>
          )}
          <button type="button" className="cl-new" onClick={() => setNewDealOpen(true)}>Novo negócio</button>
        </div>
      </div>
      {notice && <div className="emp-notice">{notice}</div>}
      {newDealOpen && <DealModal
        key={company.id}
        initialCompany={company}
        onClose={() => setNewDealOpen(false)}
        onSaved={() => {
          setNewDealOpen(false)
          load().then(setCompany).catch(() => setNotice('Negócio salvo. Atualize a página para recarregar os negócios da empresa.'))
        }}
      />}

      <div className="emp-grid">
        <section className="cl-card emp-section">
          <h3>Dados cadastrais</h3>
          <div className="emp-fields">
            <Field label="Nome fantasia" value={company.nome_fantasia} />
            <Field label="Tipo" value={company.tipo === 'filial' ? 'Filial' : company.tipo === 'matriz' ? 'Matriz' : null} />
            <Field label="Data de abertura" value={fmtDate(company.data_abertura)} />
            <Field label="Situação desde" value={fmtDate(company.data_situacao_cadastral)} />
            <Field label="Motivo da situação" value={company.motivo_situacao_cadastral} />
            {company.situacao_especial && <Field label="Situação especial" value={[company.situacao_especial, fmtDate(company.data_situacao_especial) && `desde ${fmtDate(company.data_situacao_especial)}`].filter(Boolean).join(' · ')} />}
            <Field label="Natureza jurídica" value={company.natureza_juridica} />
            <Field label="Porte" value={PORTE[company.porte]} />
            <Field label="Simples Nacional" value={company.opcao_simples == null ? null : company.opcao_simples ? 'Optante' : 'Não optante'} />
            <Field label="Capital social" value={fmtMoney(company.capital_social)} />
            <Field label="Segmento" value={company.segmento} />
            <Field label="Domínio" value={company.dominio} />
          </div>
          <div className="emp-fields emp-fields-wide">
            <Field label="Endereço" value={endereco} />
          </div>
        </section>

        <section className="cl-card emp-section">
          <h3>Contatos</h3>
          {company.contatos.length ? (
            <ul className="emp-list">
              {company.contatos.map((c) => (
                <li key={c.id}>
                  <span className="emp-field-label">{CANAL[c.channel_type] || c.channel_type}{c.is_primary ? ' · principal' : ''}</span>
                  <span className="emp-field-value">{c.value}</span>
                </li>
              ))}
            </ul>
          ) : <p className="emp-empty">Nenhum contato cadastrado.</p>}

          <h3>Atividades (CNAE)</h3>
          {company.cnaes.length ? (
            <ul className="emp-list">
              {company.cnaes.map((c) => (
                <li key={c.codigo}>
                  <span className="emp-field-label">{fmtCnae(c.codigo)}{c.is_principal ? ' · principal' : ''}</span>
                  <span className="emp-field-value">{c.descricao || '—'}</span>
                </li>
              ))}
            </ul>
          ) : <p className="emp-empty">Nenhum CNAE cadastrado.</p>}
        </section>
      </div>

      <section className="cl-card emp-section">
        <h3>Sócios</h3>
        {company.socios.length ? (
          <div className="cl-table-scroll">
            <table className="cl-table emp-static-table">
              <thead><tr><th>Nome</th><th>Documento</th><th>Qualificação</th><th>Entrada</th></tr></thead>
              <tbody>
                {company.socios.map((s) => (
                  <tr key={s.id}>
                    <td>{s.nome}</td>
                    <td>{s.tipo_documento === 'cnpj' ? fmtCnpj(s.documento) : s.documento || '—'}</td>
                    <td>{s.qualificacao || '—'}</td>
                    <td>{fmtDate(s.data_entrada) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="emp-empty">Nenhum sócio cadastrado.</p>}
      </section>

      {company.cnpj && (
        <section className="cl-card emp-section">
          <h3>Matriz e filiais</h3>
          {filiaisErr ? <p className="emp-empty">{filiaisErr}</p> : !filiais ? <p className="emp-empty">Consultando a base pública…</p> : (
            <>
              <div className="cl-table-scroll">
                <table className="cl-table emp-static-table">
                  <thead><tr><th>CNPJ</th><th>Tipo</th><th>Nome fantasia</th><th>Cidade/UF</th><th>Situação</th><th /></tr></thead>
                  <tbody>
                    {filiais.items.map((f) => (
                      <tr key={f.cnpj}>
                        <td>{fmtCnpj(f.cnpj)}</td>
                        <td>{f.tipo === 'filial' ? 'Filial' : 'Matriz'}</td>
                        <td>{f.nome_fantasia || '—'}</td>
                        <td>{[f.cidade, f.uf].filter(Boolean).join(' / ') || '—'}</td>
                        <td>{SITUACAO[f.situacao_cadastral] || '—'}</td>
                        <td>
                          {f.cnpj === company.cnpj ? <span className="emp-badge">esta empresa</span>
                            : f.already_imported ? <span className="emp-badge">na organização</span>
                              : <button type="button" className="emp-link" disabled={!!importingCnpj} onClick={() => importarFilial(f.cnpj)}>{importingCnpj === f.cnpj ? 'Importando…' : 'Importar'}</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filiais.capped && <p className="emp-empty">Mostrando os 200 primeiros estabelecimentos.</p>}
            </>
          )}
        </section>
      )}

      <section className="cl-card emp-section">
        <h3>Negócios</h3>
        {company.negocios.length ? (
          <div className="cl-table-scroll">
            <table className="cl-table">
              <thead><tr><th>Negócio</th><th>Pipeline</th><th>Etapa</th><th>Status</th><th>Valor</th></tr></thead>
              <tbody>
                {company.negocios.map((d) => (
                  <tr key={d.id} tabIndex={0}
                    onClick={() => navigate('/comercial/pipelines', { state: { openDealId: d.id } })}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate('/comercial/pipelines', { state: { openDealId: d.id } }) }}>
                    <td>{d.titulo || 'Negócio sem título'}</td>
                    <td>{d.pipeline_nome || '—'}</td>
                    <td>{d.stage_nome || '—'}</td>
                    <td>{STATUS_NEGOCIO[d.status] || d.status || '—'}</td>
                    <td>{fmtMoney(d.valor) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="emp-empty">Nenhum negócio vinculado a esta empresa.</p>}
      </section>
    </Layout>
  )
}
